import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconChevronDown, IconChevronRight, IconEye, IconEyeOff, IconGripVertical, IconLayout2, IconPencil, IconPlus, IconTrash, IconTrashFilled } from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { uploadImage } from '../../../../api/api.global';
import { getApi, postApi } from '../../../../api/call.api';
import { urlApi } from '../../../../api/url.api';
import IconButton from '../../../../components/Buttons/IconButton';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';
import ToggleBtn from '../../../../components/Toggle/ToggleBtn';
import { loadingHide, loadingShow } from '../../../../loadingUi/loading.controller';
import AddLinkToBlock from '../AddLinkToBlock';
import EditLinkOfBlock from '../EditLinkOfBlock';
import { useDispatch, useSelector } from 'react-redux';
import { disable, enable } from '../../../../redux/features/blockEdit/blockEditSlice';
import { ensureHttpPrefix } from '../../../../utils/url.utils';

const GridDragBlock = React.memo(({ data, callback, dragButton }) => {

    const { pageId } = useParams();
    const [addNewImage, setAddNewImage] = useState(false);
    const [file, setFile] = useState(null);
    const [isLink, setIsLink] = useState(false)
    const imageUploadInputRef = useRef(null);
    const [localData, setLocalData] = useState(data?.images || []);
    const [isListShow, setIsListShow] = useState(false);

    async function updateStatus(status) {
        let url = `${urlApi.updateBlockStatus}${pageId}/${data?._id}/${status}`;
        loadingShow();
        let resp = await getApi(url);
        loadingHide();
        if (resp.responseCode === 200) {
            if (callback) {
                callback();
            }
            toast.success(resp.message);
        } else {
            toast.error(resp.message);
        }
    }

    useEffect(() => {
        setLocalData(data?.images)
    }, [data]);

    async function handleDragEnd(event) {
        const { active, over } = event;
        if (over && active?.id !== over?.id) {
            let local = [...localData];
            const activeIndex = local.findIndex(item => item._id === active.id);
            const overIndex = local.findIndex(item => item._id === over.id);
            let newArr = arrayMove(local, activeIndex, overIndex);
            setLocalData(newArr);
            let resp = await postApi(newArr, urlApi.blockImagePositionChange + pageId + "/" + data?._id);
            if (resp.responseCode === 200) {
                callback();
            } else {
                toast.error(resp.message)
            }
        }
    }

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        const maxSizeInBytes = 1024 * 1024 * 5;
        if (file && file.size > maxSizeInBytes) {
            toast.error('File size exceeds the limit of 5MB.');
            setFile(null);
            e.target.value = null;
        } else {
            setFile(file);
        }
    }

    const onCancel = () => {
        setFile(null);
        setAddNewImage(false);
    }

    const handleAddImage = async () => {
        if (file) {
            let postData = {
                image: await uploadImage(file, 'advy-pages'),
                link: document.getElementById('link')?.value?.trim(),
                title: document.getElementById('title').value?.trim(),
                description: document.getElementById('description')?.value?.trim(),
                destinationUrl: ensureHttpPrefix(document.getElementById('destinationUrl')?.value?.trim()),
            }
            let resp = await postApi(postData, urlApi.addBlockImage + pageId + '/' + data?._id);
            if (resp.responseCode === 200) {
                if (callback) {
                    callback();
                }
                setFile(null);
                setAddNewImage(false);
            } else {
                toast.error(resp.message)
            }
        } else {
            toast.error('Please select an image.')
            return;
        }
    }

    async function handleUpdateImageStatus(imageData, status) {
        loadingShow();
        let url = `${urlApi.updateBlockImageStatus}${pageId}/${data?._id}/${imageData?._id}/${status}`;
        let resp = await getApi(url);
        loadingHide();
        if (resp.responseCode === 200) {
            if (callback) {
                callback();
            }
            toast.success(resp.message);
        } else {
            toast.error(resp.message);
        }
    }

    const handleDeleteDraggableItem = (item) => {
        let localPageData = [...localData]
        const filteredLinks = localPageData?.filter((link) => link._id !== item?._id);
        localPageData = filteredLinks;
        setLocalData(localPageData);
        handleUpdateImageStatus(item, 3);
    }


    return (
        <div className='w-full'>

            <div className='flex gap-3 items-center w-full mb-3'>
                {dragButton}
                <div className="flex gap-1 items-center">
                    <IconLayout2 size={16} />
                    <p className="mb-0 font-medium text-xs">Grid</p>
                </div>
                <div className="ms-auto"></div>
                <ConfirmationModal buttonComponent={<IconButton icon={<IconTrash size={18} />} />} confirmationText={"Are you sure want ro remove this block?"} onConfirm={() => updateStatus(3)} />
                <ToggleBtn state={data?.status == 1} onClickFunction={() => {
                    if (data?.status == 1) {
                        updateStatus(2)
                    } if (data?.status == 2) {
                        updateStatus(1)
                    }
                }} />
            </div>

            <div className='ps-4'>

                <div className="flex mb-2 gap-2 items-center border-b-2 pb-1 cursor-pointer" onClick={() => setIsListShow(prev => !prev)}>
                    {isListShow ? <IconChevronDown size={20} /> : <IconChevronRight size={20} />}
                    <p className='mb-0 text-sm'> {localData?.length} of 9 images added</p>
                </div>

                {isListShow && (localData?.length > 0 && <DndContext onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]} collisionDetection={closestCenter}>
                    <SortableContext items={localData?.map(item => item?._id)} strategy={verticalListSortingStrategy}>
                        {
                            localData?.map((item, index) => {
                                return (
                                    <DragItem key={item._id}
                                        itemData={item}
                                        handleDeleteDraggableItem={(itemData) => handleDeleteDraggableItem(itemData)}
                                        pageId={pageId}
                                        blockId={data?._id}
                                        callback={() => callback()}
                                        updateStatusFunction={(imageData, status) => handleUpdateImageStatus(imageData, status)}
                                    />
                                )
                            })
                        }
                    </SortableContext>
                </DndContext>)}

                {
                    addNewImage && <div className='border-2 p-3 rounded-xl'>
                        <div className="mb-2">
                            <input type="file" accept="image/*" name="" id="" className="hidden" onChange={handleImageChange} ref={imageUploadInputRef} />
                            <button onClick={() => imageUploadInputRef.current.click()}>
                                <img src={file ? URL.createObjectURL(file) : (process.env.PUBLIC_URL + "/images/image-upload.png")} id='link-image-preview' className='cursor-pointer max-w-[180px] max-h-[200px] rounded-lg object-cover' alt="" />
                            </button>
                            <p className="text-xs text-gray-400">*Maximum size: 5MB</p>
                        </div>
                        <div className='w-full'>
                            <p className="text-md font-semibold mb-1.5">Title & Description</p>
                            <input type="text" className="form-control mb-2 w-full" placeholder="Enter Title" id='title' />
                            <input type="text" className="form-control w-full" placeholder="Enter Description (Optional)" id='description' />
                        </div>

                        <div className='flex gap-3 items-center mt-3'>
                            <button className={`matchCornerButton ${isLink ? 'on' : 'off'}`} onClick={() => setIsLink(prev => !prev)}>
                                <span className="pin" />
                            </button> <label htmlFor="range" className='text-md font-semibold'>Add a link</label>
                        </div>

                        {isLink && <div>
                            <AddLinkToBlock />
                        </div>}

                        <div className="flex justify-end items-center mt-3 gap-3">
                            <button className="font-semibold text-sm border-2 border-primary px-3 py-1.5 rounded-full" onClick={() => onCancel()}>Cancel</button>
                            <button className="font-semibold text-sm text-white bg-primary px-3 py-2 rounded-full" onClick={() => handleAddImage()}>Create</button>
                        </div>
                    </div>
                }

                {(!addNewImage && localData?.length < 9) && <button className="primary-outline-btn" onClick={() => setAddNewImage(true)}> <IconPlus size={18} /> Add Another Image</button>}

            </div>
        </div>
    )
})

export default GridDragBlock;

export const DragItem = ({ itemData, handleDeleteDraggableItem, pageId, blockId, callback, updateStatusFunction }) => {
    const { attributes, listeners, setNodeRef, transform, transition, } = useSortable({ id: itemData?._id });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
    };

    const [isLink, setIsLink] = useState(false);
    const imageUploadInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const dispatch = useDispatch();
    const isEditable = useSelector((state) => state.isBlockEdit?.value);
    const gridBlockName = 'grid-block-' + itemData?._id

    const handleOnEditClick = () => {
        dispatch(enable(gridBlockName))
    }

    const handleImageChange = (e) => {
        setFile(e.target.files[0]);
    }

    async function update() {
        loadingShow();
        let url = `${urlApi.updateBlockDetails}${pageId}/${blockId}/${itemData?._id}`;
        let postData = {
            title: document.getElementById('title-' + itemData?._id).value?.trim(),
            description: document.getElementById('description-' + itemData?._id).value?.trim(),
            destinationUrl: ensureHttpPrefix(document.getElementById('destinationUrl-' + itemData?._id)?.value?.trim()),
        }
        if (file || itemData?.image) {
            postData.image = file ? await uploadImage(file, 'advy-pages') : itemData?.image
        }

        let resp = await postApi(postData, url);
        loadingHide();
        if (resp.responseCode === 200) {
            if (callback) {
                callback();
            }
            dispatch(disable(''));
            toast.success(resp.message);
        } else {
            toast.error(resp.message);
        }
    }

    return (
        <>
            {isEditable == gridBlockName ? <div className='border-2 rounded-xl p-3 gap-3 mb-2'>
                <div className="mb-2">
                    <input type="file" accept="image/*" name="" id="" className="hidden" onChange={handleImageChange} ref={imageUploadInputRef} />
                    <button onClick={() => imageUploadInputRef.current.click()}>
                        <img src={file ? URL.createObjectURL(file) : itemData?.image ? (itemData?.image) : (process.env.PUBLIC_URL + "/images/image-upload.png")} id='link-image-preview' className='cursor-pointer max-w-[180px] max-h-[200px] rounded-lg object-cover' alt="" />
                    </button>
                </div>

                <div className='w-full'>
                    <p className="text-md font-semibold mb-1.5">Title & Description</p>
                    <input type="text" className="form-control mb-2 w-full" placeholder="Enter Title" id={'title-' + itemData?._id} defaultValue={itemData?.title} maxLength={255} />
                    <input type="text" className="form-control w-full" placeholder="Enter Description (Optional)" id={'description-' + itemData?._id} defaultValue={itemData?.description} maxLength={140} />
                </div>

                <div className='flex gap-3 items-center mt-3 mb-2'>
                    <button className={`matchCornerButton ${isLink ? 'on' : 'off'}`} onClick={() => setIsLink(prev => !prev)}>
                        <span className="pin" />
                    </button> <label htmlFor="range" className='text-md font-semibold'>Add a link</label>
                </div>

                {isLink && <div className='mb-2'>
                    <EditLinkOfBlock blockData={itemData} />
                </div>}

                <div className="flex justify-end items-center mt-3 gap-3">
                    <button className="font-semibold text-sm border-2 border-primary px-3 py-1.5 rounded-full" onClick={() => dispatch(disable(''))}>Cancel</button>
                    <button className="font-semibold text-sm text-white bg-primary px-3 py-2 rounded-full" onClick={() => update()}>Update</button>
                </div>
            </div>
                :
                <div ref={setNodeRef} style={style}>
                    <div className='flex items-center flex-wrap py-0.5 gap-3 mb-2' style={{ borderBottom: '2px solid #e7e7e7' }}>
                        <button className='cursor-grabbing' {...attributes} {...listeners}>
                            <IconGripVertical />
                        </button>
                        <div className="flex flex-wrap gap-3 mb-2" >
                            <img src={itemData?.image ? (itemData?.image) : process.env.PUBLIC_URL + "/images/image-upload.png"} alt="" className='h-16 rounded-md' />
                            <div>
                                <p className='mb-1 font-semibold'>{itemData?.title}</p>
                                <p className="mb-0 text-xs text-[#686C8F]">{itemData?.description}</p>
                                <a href={itemData?.link} className='text-xs text-primary hover:text-primary-dark'>{itemData?.link}</a>
                            </div>
                        </div>

                        <div className='flex items-center justify-center ms-auto gap-2'>
                            {isEditable == '' ? <button className='ms-auto' onClick={() => handleOnEditClick(true)}> <IconPencil color='var(--primary)' size={22} /> </button> : <div className='ms-auto'></div>}
                            {itemData?.status == 1 && <button onClick={() => updateStatusFunction(itemData, 2)}> <IconEye color='var(--primary)' size={22} /></button>}
                            {itemData?.status == 2 && <button onClick={() => updateStatusFunction(itemData, 1)}> <IconEyeOff color='#686C8F' size={22} /></button>}
                            <ConfirmationModal buttonComponent={<button><IconTrashFilled color='#EB568E' /></button>} confirmationText={"Are you sure want to delete this image?"} onConfirm={() => handleDeleteDraggableItem(itemData)} />
                        </div>
                    </div>
                </div>}
        </>

    )
}